import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Typography,
} from "@mui/material";

import VideoPlayer from "./VideoPlayer";
import VideoEditorSidebarWrapper from "./VideoEditorSidebar/VideoEditorSidebarWrapper";
import { DRAWER_WIDTH } from "./constants";
import VideoEditor from "./VideoEditor/VideoEditor";
import VideoLogoEditor from "./VideoEditor/components/VideoLogoEditor/VideoLogoEditor";
import useEditVideoFile from "../backend/useEditVideoFile";
import { useAppContext } from "../hooks/useAppContext";
import CircularProgressWithLabel from "./CircularProgressWithLabel";
import checkIfcanPlayVideo from "../utils/checkIfcanPlayVideo";

// 1. Import the Autonomous Brain
import { useKernel } from "../hooks/AIKernel"; 

const VideoEditorWrapper: React.FC = () => {
  const { isProcessing } = useAppContext();
  const { transcodeVideo, progress } = useEditVideoFile();
  
  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel(); 
  
  const [videoFile, setVideoFile] = useState("");

  // 3. Dynamic Theme Evaluation based on AI Kernel
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const bgColor = isDarkMode ? "#121212" : "#ffffff";
  const textColor = isDarkMode ? "#ffffff" : "#000000";

  const label = useMemo(() => {
    if (isProcessing && progress) {
      return (
        <>
          <Typography variant="h5" gutterBottom color={textColor}>
            Video transcoding
            {agentState?.ui?.simplifyAnimations && " (Accelerated Mode)"}
          </Typography>
          <CircularProgressWithLabel size={56} value={progress} />
        </>
      );
    }

    if (isProcessing) {
      return (
        <>
          <Typography variant="h5" gutterBottom color={textColor}>
            Processing Media...
          </Typography>
          <CircularProgress size={56} />
        </>
      );
    }

    return (
      <Typography variant="h5" color={textColor}>
        Click or Drop a Video File Here
      </Typography>
    );
  }, [isProcessing, progress, textColor, agentState]);

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (isProcessing) return;

      if (event.dataTransfer.files && event.dataTransfer.files[0]) {
        const file = event.dataTransfer.files[0];

        if (!file.type.startsWith("video/")) {
          // TELEMETRY: User dropped the wrong file type (Friction / Cognitive Load)
          if (reportBehavior) reportBehavior("invalid_file_drop", 2, "behavior");
          return;
        }

        const isPlayable = await checkIfcanPlayVideo(file);
        const reader = new FileReader();

        reader.onload = async () => {
          let videoBlob = URL.createObjectURL(file);

          if (!isPlayable) {
            const transcodedVideo = await transcodeVideo(videoBlob);
            if (transcodedVideo) {
              videoBlob = transcodedVideo;
            }
          }

          if (videoBlob) {
            // TELEMETRY: User successfully loaded a video (High Intent)
            if (reportBehavior) reportBehavior("video_upload_success", 10, "interest");
            setVideoFile(videoBlob);
          }
        };

        reader.readAsDataURL(file);
      }
    },
    [isProcessing, transcodeVideo, reportBehavior]
  );

  const handleClick = useCallback(() => {
    if (isProcessing) return;

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "video/*";
    fileInput.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        const isPlayable = await checkIfcanPlayVideo(target.files[0]);
        let videoBlob = URL.createObjectURL(target.files[0]);

        const transcodedVideo = await transcodeVideo(videoBlob);
        if (transcodedVideo) {
          videoBlob = transcodedVideo;
        }

        if (videoBlob) {
          // TELEMETRY: User successfully loaded a video (High Intent)
          if (reportBehavior) reportBehavior("video_upload_success", 10, "interest");
          setVideoFile(videoBlob);
        }
      }
    };
    fileInput.click();
  }, [isProcessing, transcodeVideo, reportBehavior]);

  return !videoFile ? (
    <Box
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        padding: "20px",
        textAlign: "center",
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        // ADAPTIVE UI: Background transitions smoothly based on the AI's theme logic
        background: bgColor,
        color: textColor,
        transition: "background-color 0.5s ease, color 0.5s ease",
        // Visual affordance increases if user is frustrated
        border: agentState?.ui?.highlightCTAs ? `2px dashed ${textColor}` : "none", 
      }}
    >
      {label}
    </Box>
  ) : (
    <Container
      maxWidth="lg"
      sx={{
        p: "0 !important",
        height: "100dvh",
        display: "flex",
        background: bgColor,
        transition: "background-color 0.5s ease",
      }}
    >
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)`, xs: "100%" },
        }}
      >
        <div style={{ position: "relative" }}>
          <VideoPlayer videoSrc={videoFile} />
          <VideoLogoEditor />
        </div>
        <Divider sx={{ borderColor: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }} />
        <VideoEditor />
      </Box>
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH, xs: 0 }, flexShrink: { md: 0 } }}
        aria-label="transcriptions drawer"
      >
        <VideoEditorSidebarWrapper />
      </Box>
    </Container>
  );
};

export default VideoEditorWrapper;